/**
 * ZeroCreditsBanner — Nexus v29
 *
 * Renders inline inside the chat above the composer WHEN the user's
 * credit balance is 0. Two variants:
 *
 *   1. First-time user (no purchase yet, welcome_offer available)
 *      → Conversational assistant message + "Aprovechar oferta" CTA
 *      → Opens Stripe Checkout directly for welcome_offer package
 *
 *   2. Returning user (already purchased something before, or offer
 *      no longer available)
 *      → Conversational assistant message + "Recargar créditos" CTA
 *      → Opens the unified CreditsPanel modal
 *
 * The banner is DISMISSABLE per-session (localStorage) so we don't
 * hammer users who choose to explore other workspaces first, but it
 * ALWAYS reappears when they try to send a message with 0 credits.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Coins, Loader2, X } from 'lucide-react';
import { useCredits } from '../../contexts/CreditsContext';
import { apiService } from '../../services/api';
import { toast } from 'sonner';

type Props = {
  /** Where the user should return AFTER the payment flow. */
  returnTo?: string;
  /** Hide by default; only show when explicitly required (e.g. user
   *  attempted to send with 0 credits). */
  forceShow?: boolean;
};

const DISMISS_KEY = 'nexus_zero_credits_banner_dismissed_at';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — banner reaparece al día siguiente.

// Sprint P0.1 v1.44 · Countdown 39:00 de la oferta única.
// El timer se ancla a la PRIMERA vez que el usuario ve la oferta
// (localStorage) para que el tiempo no se resetee entre navegaciones.
// Cuando expira, oculta la sección con un mensaje elegante.
const COUNTDOWN_KEY = 'nexus_welcome_offer_started_at';
const COUNTDOWN_TOTAL_MS = 39 * 60 * 1000; // 39 minutos

const OfferCountdown: React.FC = () => {
  const [remainingMs, setRemainingMs] = useState<number>(() => {
    if (typeof window === 'undefined') return COUNTDOWN_TOTAL_MS;
    const anchor = Number(localStorage.getItem(COUNTDOWN_KEY) || 0);
    if (!anchor) {
      const now = Date.now();
      localStorage.setItem(COUNTDOWN_KEY, String(now));
      return COUNTDOWN_TOTAL_MS;
    }
    return Math.max(0, COUNTDOWN_TOTAL_MS - (Date.now() - anchor));
  });

  useEffect(() => {
    if (remainingMs <= 0) return;
    const t = window.setInterval(() => {
      setRemainingMs((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => window.clearInterval(t);
  }, [remainingMs]);

  const mm = Math.floor(remainingMs / 60000);
  const ss = Math.floor((remainingMs % 60000) / 1000);
  const label = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  return (
    <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-cyan-400/25 bg-cyan-500/5 px-2.5 py-1.5">
      <span className="text-[10px] uppercase tracking-widest text-cyan-300/80">
        Termina en
      </span>
      <span
        data-testid="welcome-offer-countdown"
        className="font-mono tabular-nums text-sm font-semibold text-cyan-200"
      >
        {label}
      </span>
    </div>
  );
};

export const ZeroCreditsBanner: React.FC<Props> = ({ returnTo, forceShow }) => {
  const { balance, welcomeOffer, openCreditsPanel, refresh } = useCredits();
  const [redirecting, setRedirecting] = useState(false);
  // v1.15 · point #16 — banner closable per session (localStorage TTL).
  // Cuando forceShow=true (usuario intenta enviar con 0 créditos) SIEMPRE
  // reaparece; el dismiss solo aplica a la aparición pasiva.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && (Date.now() - t) < DISMISS_TTL_MS;
  });

  const showWelcome = !!(
    welcomeOffer &&
    welcomeOffer.available &&
    (balance ?? 0) <= 0
  );
  const showRegular = (balance !== null && balance <= 0) && !showWelcome;

  // Refresh once on mount to have the latest offer state.
  useEffect(() => {
    if ((balance ?? 0) <= 0) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takeWelcomeOffer = useCallback(async () => {
    if (redirecting) return;
    setRedirecting(true);
    try {
      const res = await apiService.createCheckoutSession('welcome_offer', {
        returnTo,
      });
      window.location.href = res.checkout_url;
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'No pudimos iniciar el pago.';
      toast.error(msg);
      setRedirecting(false);
    }
  }, [returnTo, redirecting]);

  if (!forceShow && !showWelcome && !showRegular) return null;
  if (!showWelcome && !showRegular) return null;
  // Dismissed by user → only respect if not forced.
  if (dismissed && !forceShow) return null;

  const closeBanner = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid={showWelcome ? 'welcome-offer-banner' : 'zero-credits-banner'}
      className="mx-3 mb-3 rounded-2xl border border-cyan-400/25 bg-slate-950/70 backdrop-blur-xl overflow-hidden relative"
    >
      {!forceShow && (
        <button
          type="button"
          onClick={closeBanner}
          data-testid="zero-credits-banner-close"
          aria-label="Cerrar"
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full grid place-items-center text-slate-400 hover:text-slate-100 hover:bg-white/5 transition"
        >
          <X size={13} />
        </button>
      )}
      <div className="p-4 sm:p-5 flex items-start gap-3">
        <div className="shrink-0 h-9 w-9 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
          {showWelcome ? (
            <Sparkles size={16} className="text-cyan-300" />
          ) : (
            <Coins size={16} className="text-cyan-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {showWelcome ? (
            <>
              <div className="text-sm font-medium text-slate-100">
                ✨ Activa hoy el Plan Estándar por solo $2.99
              </div>
              <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                Con esta activación recibirás también{' '}
                <span className="text-cyan-300 font-medium">
                  +{welcomeOffer!.package.credits} créditos
                </span>{' '}
                para seguir creando aplicaciones inmediatamente.{' '}
                <span className="text-slate-400/80">Oferta válida únicamente por tiempo limitado.</span>{' '}
                <span className="text-slate-500 text-xs block mt-1">
                  Cuando esta promoción finalice, las recargas volverán a su precio normal.
                </span>
              </p>
              <OfferCountdown />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={takeWelcomeOffer}
                  disabled={redirecting}
                  data-testid="welcome-offer-cta"
                  className={[
                    'inline-flex items-center gap-1.5 rounded-lg',
                    'bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950',
                    'font-semibold text-sm px-4 py-2',
                    'shadow-[0_12px_25px_-10px_rgba(34,211,238,0.55)]',
                    redirecting ? 'opacity-70 cursor-wait' : 'hover:from-cyan-300 hover:to-cyan-200',
                  ].join(' ')}
                >
                  {redirecting ? (
                    <><Loader2 size={14} className="animate-spin" /> Abriendo Stripe…</>
                  ) : (
                    <>Activar por $2.99 →</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => openCreditsPanel(returnTo)}
                  className="text-xs text-slate-500 hover:text-cyan-300 transition-colors underline underline-offset-4"
                  data-testid="welcome-offer-see-packages"
                >
                  Ver todos los paquetes
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-100">
                Parece que ya utilizaste todos tus créditos disponibles.
              </div>
              <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                Recarga créditos para continuar exactamente donde nos
                quedamos. Tu conversación y el contexto permanecerán
                intactos.
              </p>
              <button
                type="button"
                onClick={() => openCreditsPanel(returnTo)}
                data-testid="zero-credits-recharge-cta"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950 font-semibold text-sm px-4 py-2 shadow-[0_12px_25px_-10px_rgba(34,211,238,0.55)] hover:from-cyan-300 hover:to-cyan-200"
              >
                <Coins size={14} />
                Recargar créditos
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZeroCreditsBanner;
